import { provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import {
    ApplicationConfig,
    inject,
    LOCALE_ID,
    provideAppInitializer,
    provideBrowserGlobalErrorListeners,
    provideZoneChangeDetection
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { provideTranslateService, TranslateParser, TranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { provideStore } from '@ngxs/store';
import { provideIconConfig } from '@siemens/element-ng/icon';
import { SI_LOCALE_CONFIG, SiLocaleId, SiLocaleService } from '@siemens/element-ng/localization';
import {
    provideNgxTranslateForElement,
    provideMissingTranslationHandlerForElement
} from '@siemens/element-translate-ng/ngx-translate';
import {
    initializeLanguage,
    initializeMsal,
    provideInterceptors,
    provideMsalConfiguration,
    provideMsalServices
} from '@tran-monorepo/frontend/auth';
import { DEFAULT_LOCALE, localeConfig } from '@tran-monorepo/frontend/core';
import {
    APPLICATION_API_URL,
    KPIS_API_URL,
    ORG_UNITS_API_URL,
    SCD_API_URL,
    UNITS_API_URL
} from '@tran-monorepo/frontend/services';
import { LOCATIONS_API_URL } from '@tran-monorepo/frontend/services/location.service';
import { SUBJECT_AREAS_API_URL } from '@tran-monorepo/frontend/services/subject-areas.service';
import { GlobalTranslateParser } from '@tran-monorepo/frontend/translate';
import { environment } from '../environments/environment';
import { appRoutes } from './app.routes';

export const appConfig: ApplicationConfig = {
    providers: [
        provideBrowserGlobalErrorListeners(),
        provideZoneChangeDetection({ eventCoalescing: true }),
        provideRouter(appRoutes, withComponentInputBinding()),
        provideAnimations(),
        provideIconConfig({ disableSvgIcons: false }),
        provideHttpClient(withXhr(), withInterceptorsFromDi()),

        // Locale configuration for Element framework
        { provide: LOCALE_ID, useClass: SiLocaleId, deps: [SiLocaleService] },
        { provide: SI_LOCALE_CONFIG, useValue: localeConfig },

        // Locations API URL configuration
        { provide: LOCATIONS_API_URL, useValue: `${environment.apiUrl}/v1/locations` },

        // Org Units API URL configuration
        { provide: ORG_UNITS_API_URL, useValue: `${environment.apiUrl}/v1/org-units` },

        // Subject Areas API URL configuration
        { provide: SUBJECT_AREAS_API_URL, useValue: `${environment.apiUrl}/v1/subject-areas` },

        // KPIs API URL configuration
        { provide: KPIS_API_URL, useValue: `${environment.apiUrl}/v1/kpis` },

        // Units API URL configuration
        { provide: UNITS_API_URL, useValue: `${environment.apiUrl}/v1/units` },

        // Application Files API URL configuration
        { provide: APPLICATION_API_URL, useValue: `${environment.apiUrl}/v1/applications` },

        // SCD API URL configuration
        { provide: SCD_API_URL, useValue: `${environment.apiUrl}/v1/scd` },

        // Translation configuration with ngx-translate
        provideTranslateService({
            loader: provideTranslateHttpLoader({
                prefix: '/assets/i18n/',
                suffix: `.json?hash=${environment.hash}`
            }),
            parser: { provide: TranslateParser, useClass: GlobalTranslateParser },
            fallbackLang: DEFAULT_LOCALE,
            missingTranslationHandler: provideMissingTranslationHandlerForElement()
        }),

        // Enable ngx-translate for Element components
        provideNgxTranslateForElement(),

        provideMsalConfiguration({ msalConfig: environment.msalConfig, apiConfig: environment.apiConfig }),
        provideMsalServices(),
        provideInterceptors(),
        provideAppInitializer(() => {
            const initializerFn = initializeMsal(inject(MsalService));
            return initializerFn();
        }),
        provideAppInitializer(() => {
            const initializerFn = initializeLanguage(inject(TranslateService), inject(SiLocaleService));
            return initializerFn();
        }),
        provideStore([], {
            developmentMode: !environment.production,
            compatibility: {
                strictContentSecurityPolicy: true
            }
        })
    ]
};
